import { CartAddEvent, CartErrorEvent } from '@theme/events';

var recentlyViewedKey = 'suncoViewedProducts';
var sessionCollectionKey = 'suncoLastCollection';
var sessionCollectionTrailKey = 'suncoCollectionTrail';
var productNavigationTopKey = 'suncoProductNavigationTop';
var mobileQuickAddMedia = window.matchMedia('(max-width: 749px)');
var mobileQuickAddSuccessDuration = 2200;

function parseJsonScript(element) {
  if (!element?.textContent) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (_error) {
    return null;
  }
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

function getCartStateUrl() {
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
}

async function fetchCartState() {
  try {
    const response = await fetch(getCartStateUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (errorObject) {
    console.error(errorObject);
    return null;
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeQuickAddValue(value) {
  return String(value ?? '').trim().toLowerCase();
}

function getQuickAddData(card) {
  return parseJsonScript(card?.querySelector('[data-sunco-mobile-quick-add-data]'));
}

function getOptionIndex(data, optionName) {
  return data?.options?.findIndex((option) => normalizeQuickAddValue(option.name) === normalizeQuickAddValue(optionName)) ?? -1;
}

function optionNamesMatch(first, second) {
  const normalizedFirst = normalizeQuickAddValue(first);
  const normalizedSecond = normalizeQuickAddValue(second);
  return Boolean(
    normalizedFirst &&
      normalizedSecond &&
      (normalizedFirst === normalizedSecond ||
        normalizedFirst.includes(normalizedSecond) ||
        normalizedSecond.includes(normalizedFirst))
  );
}

function getSelectedSwatchOption(card) {
  const swatch = card?.querySelector('[data-sunco-card-swatch].is-active');
  if (!swatch) return null;

  return {
    name: swatch.closest('[data-sunco-swatches]')?.dataset.optionName || '',
    value: swatch.dataset.title || '',
  };
}

function variantMatchesSelections(data, variant, selections, requireComplete = false) {
  if (!data?.options?.length || !variant?.options?.length) return false;

  return data.options.every((option, index) => {
    const selected = selections?.[option.name];
    if (!selected) return !requireComplete;
    return normalizeQuickAddValue(variant.options[index]) === normalizeQuickAddValue(selected);
  });
}

function findQuickAddVariant(data, selections, requireComplete = true) {
  return data?.variants?.find((variant) => (
    variant.available && variantMatchesSelections(data, variant, selections, requireComplete)
  )) || null;
}

function getQuickAddBaseSelections(card, data) {
  const selections = { ...(card.suncoQuickAddSelections || {}) };
  const selectedSwatch = getSelectedSwatchOption(card);

  if (selectedSwatch?.name && selectedSwatch.value) {
    selections[selectedSwatch.name] = selectedSwatch.value;
  } else if (data?.colorOptionName) {
    delete selections[data.colorOptionName];
  }

  return selections;
}

function completeQuickAddSelections(data, selections = {}) {
  const completed = { ...selections };

  data?.options?.forEach((option) => {
    if (completed[option.name]) return;

    const variant = findQuickAddVariant(data, completed, false);
    const optionIndex = getOptionIndex(data, option.name);
    completed[option.name] = variant?.options?.[optionIndex] || option.values?.[0] || '';
  });

  return completed;
}

function getQuickAddChoiceOptions(card, data) {
  const selectedSwatch = getSelectedSwatchOption(card);

  return (data?.options || []).filter((option) => {
    if (!selectedSwatch?.name) return true;
    return !optionNamesMatch(option.name, selectedSwatch.name);
  });
}

function isQuickAddValueAvailable(data, optionName, value, selections) {
  const nextSelections = {
    ...selections,
    [optionName]: value,
  };

  return (data?.variants || []).some((variant) => (
    variant.available && variantMatchesSelections(data, variant, nextSelections, false)
  ));
}

function renderMobileQuickAddPanel(card, data, quantity = 1) {
  const panel = card.querySelector('[data-sunco-mobile-quick-add-panel]');
  if (!panel) return;

  const choiceOptions = getQuickAddChoiceOptions(card, data);
  const selections = completeQuickAddSelections(data, getQuickAddBaseSelections(card, data));
  card.suncoQuickAddSelections = selections;
  const selectedVariant = data.variants?.find((variant) => variantMatchesSelections(data, variant, selections, true));
  updateMobileQuickAddPrice(card, selectedVariant);
  setMobileQuickAddStatus(card, '');

  const optionMarkup = choiceOptions
    .map((option) => {
      const values = (option.values || [])
        .map((value) => {
          const active = normalizeQuickAddValue(selections[option.name]) === normalizeQuickAddValue(value);
          const available = isQuickAddValueAvailable(data, option.name, value, selections);

          return `
            <button
              type="button"
              class="sunco-plp-card__quick-add-option${active ? ' is-active' : ''}"
              data-sunco-mobile-quick-add-option
              data-option-name="${escapeHtml(option.name)}"
              data-option-value="${escapeHtml(value)}"
              aria-pressed="${active ? 'true' : 'false'}"
              ${available ? '' : 'disabled'}
            >
              ${escapeHtml(value)}
            </button>
          `;
        })
        .join('');

      return `
        <fieldset class="sunco-plp-card__quick-add-fieldset">
          <legend>${escapeHtml(option.name)}</legend>
          <div class="sunco-plp-card__quick-add-values">
            ${values}
          </div>
        </fieldset>
      `;
    })
    .join('');

  panel.innerHTML = `
    ${selectedVariant?.available ? '' : '<p class="sunco-plp-card__quick-add-availability" role="status">This combination is unavailable. Choose another pack size or color.</p>'}
    <div class="sunco-plp-card__quick-add-options">
      ${optionMarkup}
    </div>
    <div class="sunco-plp-card__quick-add-actions">
      <div class="sunco-plp-card__quick-add-quantity" aria-label="Quantity">
        <button type="button" data-sunco-mobile-quick-add-quantity-action="decrease" aria-label="Decrease quantity">-</button>
        <input
          type="number"
          value="${normalizeMobileQuickAddQuantity(quantity)}"
          min="1"
          inputmode="numeric"
          pattern="[0-9]*"
          data-sunco-mobile-quick-add-quantity
          aria-label="Quantity"
        >
        <button type="button" data-sunco-mobile-quick-add-quantity-action="increase" aria-label="Increase quantity">+</button>
      </div>
      <button type="button" class="sunco-plp-card__quick-add-submit" data-sunco-mobile-quick-add-submit ${selectedVariant?.available ? '' : 'disabled'}>
        Add selected
      </button>
    </div>
  `;
}

function setMobileQuickAddStatus(card, message) {
  const status = card?.querySelector('[data-sunco-mobile-quick-add-status]');
  if (status) status.textContent = message || '';
}

// Preview the exact pack total while options are open; restore the listing on close.
function updateMobileQuickAddPrice(card, variant) {
  const priceBlock = card.querySelector('.sunco-plp-card__price');
  const price = priceBlock?.querySelector('[data-sunco-card-price]');
  if (!price || !variant?.price) return;

  const sku = card.querySelector('[data-sunco-card-sku]');
  if (!card.suncoQuickAddListing) {
    card.suncoQuickAddListing = {
      priceHtml: priceBlock.innerHTML,
      sku: sku?.textContent || '',
      variantId: card.dataset.variantId,
      productUrl: card.dataset.productUrl,
      links: [...card.querySelectorAll('[data-sunco-card-link]')].map((link) => [link, link.getAttribute('href')]),
    };
  }

  price.textContent = variant.price;
  priceBlock.querySelectorAll('.sunco-plp-card__price-prefix, em').forEach((element) => {
    element.hidden = true;
  });
  const comparePrice = priceBlock.querySelector('[data-sunco-card-compare-price]');
  const savings = priceBlock.querySelector('[data-sunco-card-savings]');
  const meta = priceBlock.querySelector('[data-sunco-card-price-meta]');
  if (comparePrice) comparePrice.textContent = variant.compareAtPrice || '';
  if (savings) {
    savings.textContent = variant.savings ? `Save ${variant.savings}` : '';
    savings.hidden = !variant.savings;
  }
  if (meta) meta.hidden = !variant.compareAtPrice;
  let unitPrice = priceBlock.querySelector('[data-sunco-card-pack-unit-price]');
  if (!unitPrice && variant.unitPrice) {
    unitPrice = document.createElement('span');
    unitPrice.className = 'sunco-plp-card__pack-unit-price';
    unitPrice.dataset.suncoCardPackUnitPrice = '';
    priceBlock.append(unitPrice);
  }
  if (unitPrice) {
    unitPrice.textContent = variant.unitPrice ? `${variant.unitPrice} / unit` : '';
    unitPrice.hidden = !variant.unitPrice;
  }
  if (sku) sku.textContent = variant.sku || '';
  syncMobileQuickAddSkuCopy(card, variant.sku || '');
  card.dataset.variantId = String(variant.id);
  syncCardVariantLinks(card, variant.id);
}

function syncCardVariantLinks(card, variantId) {
  card.querySelectorAll('[data-sunco-card-link]').forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    link.href = url.toString();
  });
  const productUrl = new URL(card.dataset.productUrl, window.location.origin);
  productUrl.searchParams.set('variant', String(variantId));
  card.dataset.productUrl = productUrl.toString();
}

function syncMobileQuickAddSkuCopy(card, sku) {
  const copyButton = card.querySelector('[data-sunco-card-sku-copy]');
  if (!copyButton) return;
  copyButton.dataset.suncoCopyValue = sku;
  copyButton.setAttribute('aria-label', `Copy SKU ${sku}`);
}

function normalizeMobileQuickAddQuantity(value) {
  const quantity = Number.parseInt(value, 10);
  if (!Number.isFinite(quantity) || quantity < 1) return 1;
  return Math.min(quantity, 999);
}

function getMobileQuickAddQuantity(card) {
  const input = card?.querySelector('[data-sunco-mobile-quick-add-quantity]');
  return normalizeMobileQuickAddQuantity(input?.value);
}

function setMobileQuickAddQuantity(input, quantity) {
  if (!(input instanceof HTMLInputElement)) return;
  input.value = String(normalizeMobileQuickAddQuantity(quantity));
}

function handleMobileQuickAddQuantityAction(actionButton) {
  const panel = actionButton.closest('[data-sunco-mobile-quick-add-panel]');
  const input = panel?.querySelector('[data-sunco-mobile-quick-add-quantity]');
  if (!(input instanceof HTMLInputElement)) return;

  const currentQuantity = normalizeMobileQuickAddQuantity(input.value);
  const direction = actionButton.dataset.suncoMobileQuickAddQuantityAction;
  setMobileQuickAddQuantity(input, direction === 'increase' ? currentQuantity + 1 : currentQuantity - 1);
}

function getMobileQuickAddLabel(trigger) {
  return trigger?.querySelector('.add-to-cart-text__content span span');
}

function setMobileQuickAddButtonLabel(trigger, label) {
  const labelElement = getMobileQuickAddLabel(trigger);
  if (labelElement) {
    labelElement.textContent = label;
    return;
  }

  if (trigger) trigger.textContent = label;
}

async function addMobileQuickAddVariant(root, card, variant, trigger, quantity = 1) {
  if (!variant?.id || !variant.available || !(trigger instanceof HTMLElement)) return;

  const addQuantity = normalizeMobileQuickAddQuantity(quantity);
  const currentLabel = getMobileQuickAddLabel(trigger)?.textContent || trigger.textContent || 'Add to cart';
  const defaultLabel = trigger.dataset.suncoQuickAddLabel || currentLabel.trim() || 'Add to cart';
  trigger.dataset.suncoQuickAddLabel = defaultLabel;
  trigger.setAttribute('disabled', 'disabled');
  trigger.removeAttribute('data-added');
  setMobileQuickAddButtonLabel(trigger, 'Adding...');
  setMobileQuickAddStatus(card, '');

  const sourceId = `sunco-plp-quick-add-${card.dataset.productId || 'product'}-${variant.id}`;

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variant.id), quantity: addQuantity }],
        sections: getCartSectionIds(),
      }),
    });
    const payload = await response.json();

    if (payload.status) {
      const message = payload.message || 'Unable to add this product to cart.';
      setMobileQuickAddStatus(card, message);
      document.dispatchEvent(new CartErrorEvent(sourceId, message, payload.description, payload.errors));
      return;
    }

    trigger.dataset.added = 'true';
    setMobileQuickAddButtonLabel(trigger, 'Added');
    setMobileQuickAddStatus(card, '');
    trackPlpEvent(root, 'quick_add', {
      ...productCardPayload(card),
      variant_id: String(variant.id),
      variant_title: variant.title || '',
    });
    closeMobileQuickAddPanel(card);

    const cartState = await fetchCartState();
    document.dispatchEvent(
      new CartAddEvent(cartState || undefined, sourceId, {
        source: 'sunco-plp-quick-add',
        itemCount: cartState?.item_count ?? addQuantity,
        productId: card.dataset.productId,
        sections: payload.sections,
      })
    );
  } catch (errorObject) {
    console.error(errorObject);
    setMobileQuickAddStatus(card, 'Unable to add this product to cart.');
    document.dispatchEvent(new CartErrorEvent(sourceId, 'Unable to add this product to cart.', errorObject, errorObject));
  } finally {
    window.setTimeout(() => {
      setMobileQuickAddButtonLabel(trigger, defaultLabel);
      trigger.removeAttribute('data-added');
      trigger.removeAttribute('disabled');
      window.setTimeout(() => setMobileQuickAddStatus(card, ''), 1000);
    }, mobileQuickAddSuccessDuration);
  }
}

function closeMobileQuickAddPanel(card) {
  const panel = card?.querySelector('[data-sunco-mobile-quick-add-panel]');
  const trigger = card?.querySelector('[data-sunco-mobile-quick-add-trigger]');
  if (panel) panel.setAttribute('hidden', '');
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
  setMobileQuickAddStatus(card, '');
  card.suncoQuickAddSelections = null;
  const listing = card.suncoQuickAddListing;
  if (listing) {
    const priceBlock = card.querySelector('.sunco-plp-card__price');
    if (priceBlock) priceBlock.innerHTML = listing.priceHtml;
    const sku = card.querySelector('[data-sunco-card-sku]');
    if (sku) sku.textContent = listing.sku;
    syncMobileQuickAddSkuCopy(card, listing.sku);
    card.dataset.variantId = listing.variantId;
    card.dataset.productUrl = listing.productUrl;
    listing.links.forEach(([link, href]) => link.setAttribute('href', href));
    card.suncoQuickAddListing = null;
  }
}

mobileQuickAddMedia.addEventListener('change', (event) => {
  if (event.matches) return;
  document.querySelectorAll('[data-sunco-mobile-quick-add-panel]:not([hidden])').forEach((panel) => {
    closeMobileQuickAddPanel(panel.closest('[data-sunco-product-card]'));
  });
});

async function handleMobileQuickAddTrigger(root, trigger) {
  if (!mobileQuickAddMedia.matches) return;

  const card = trigger.closest('[data-sunco-product-card]');
  const panel = card?.querySelector('[data-sunco-mobile-quick-add-panel]');
  const data = getQuickAddData(card);
  if (!card || !panel || !data) return;

  const availableVariants = (data.variants || []).filter((variant) => variant.available);
  const choiceOptions = getQuickAddChoiceOptions(card, data);
  const choiceRequired = choiceOptions.some((option) => (option.values || []).length > 1);
  const selections = completeQuickAddSelections(data, getQuickAddBaseSelections(card, data));
  const selectedVariant = findQuickAddVariant(data, selections, true);

  if (!availableVariants.length) {
    setMobileQuickAddStatus(card, 'This product is sold out.');
    return;
  }

  if (!choiceRequired) {
    if (!selectedVariant) {
      setMobileQuickAddStatus(card, 'This combination is unavailable. Choose another color or option.');
      return;
    }
    await addMobileQuickAddVariant(root, card, selectedVariant, trigger);
    return;
  }

  if (!panel.hidden) {
    closeMobileQuickAddPanel(card);
    return;
  }

  renderMobileQuickAddPanel(card, data);
  panel.removeAttribute('hidden');
  trigger.setAttribute('aria-expanded', 'true');
  trackPlpEvent(root, 'quick_add_options_open', productCardPayload(card));
}

function handleMobileQuickAddOption(card, optionButton) {
  const data = getQuickAddData(card);
  if (!data) return;

  card.suncoQuickAddSelections = {
    ...getQuickAddBaseSelections(card, data),
    [optionButton.dataset.optionName || '']: optionButton.dataset.optionValue || '',
  };

  renderMobileQuickAddPanel(card, data, getMobileQuickAddQuantity(card));
}

async function handleMobileQuickAddSubmit(root, submitButton) {
  const card = submitButton.closest('[data-sunco-product-card]');
  const data = getQuickAddData(card);
  if (!card || !data) return;

  const selections = completeQuickAddSelections(data, getQuickAddBaseSelections(card, data));
  const selectedVariant = findQuickAddVariant(data, selections, true);
  if (!selectedVariant) {
    setMobileQuickAddStatus(card, 'Select available options.');
    return;
  }

  const trigger = card.querySelector('[data-sunco-mobile-quick-add-trigger]');
  if (trigger && trigger !== submitButton) submitButton.setAttribute('disabled', 'disabled');

  try {
    await addMobileQuickAddVariant(root, card, selectedVariant, trigger || submitButton, getMobileQuickAddQuantity(card));
  } finally {
    if (trigger && trigger !== submitButton) submitButton.removeAttribute('disabled');
  }
}

function isPlpAnalyticsEnabled(root) {
  return root?.matches?.('[data-sunco-plp]') && root.dataset.suncoPlpAnalytics !== 'false';
}

function shouldPushPlpDataLayer(root) {
  return root?.matches?.('[data-sunco-plp]') && root.dataset.suncoPlpDataLayer !== 'false';
}

function productCardPayload(card) {
  if (!card) return {};

  return {
    product_id: card.dataset.productId || '',
    product_title: card.dataset.productTitle || card.querySelector('[data-sunco-card-title]')?.textContent?.trim() || '',
    product_handle: card.dataset.productHandle || '',
    product_url: card.dataset.productUrl || card.querySelector('[data-sunco-card-link]')?.href || '',
    variant_id: card.dataset.variantId || '',
    sku: card.querySelector('[data-sunco-card-sku]')?.textContent?.trim() || '',
    card_position: card.dataset.suncoCardPosition || '',
    selected_option: card.querySelector('[data-sunco-card-selected-option]')?.textContent?.trim() || '',
    price_text: card.querySelector('.sunco-plp-card__purchase')?.innerText?.replace(/\s+/g, ' ').trim() || '',
  };
}

function filterInputPayload(input) {
  if (!input) return {};

  const filter = input.closest('.sunco-plp-filter');
  const label = filter?.querySelector('summary span')?.textContent?.trim() || '';
  const value = input.nextElementSibling?.textContent?.replace(/\s+/g, ' ').trim() || input.value || '';

  return {
    filter_label: label,
    filter_value: value,
    filter_parameter: input.name || '',
    filter_checked: Boolean(input.checked),
  };
}

function trackPlpEvent(root, name, detail = {}) {
  if (!isPlpAnalyticsEnabled(root)) return;

  const payload = {
    event: `sunco_plp_${name}`,
    event_name: name,
    collection_title: root?.dataset.collectionTitle || '',
    collection_url: root?.dataset.collectionUrl || '',
    product_layout: root?.querySelector('[data-sunco-products]')?.classList.contains('sunco-plp-products--grid')
      ? 'grid'
      : 'list',
    page_url: window.location.href,
    ...detail,
  };

  window.dispatchEvent(new CustomEvent('sunco:plp-event', { detail: payload }));

  if (shouldPushPlpDataLayer(root)) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
}

function humanizeCollectionHandle(handle) {
  const normalized = String(handle || '').trim().toLowerCase();
  const titleOverrides = {
    suncopro: 'SuncoPRO',
    commercial: 'Commercial',
    residential: 'Residential',
    outdoor: 'Outdoor',
    electrical: 'Electrical',
    deals: 'Deals',
  };

  if (titleOverrides[normalized]) return titleOverrides[normalized];

  return normalized
    .split('-')
    .filter(Boolean)
    .map((part) => {
      if (/^\d+k$/i.test(part)) return part.toUpperCase();
      if (['led', 'ufo', 'cct', 'cri', 'rgb', 'br', 'par'].includes(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function normalizeCollectionUrl(value) {
  try {
    return new URL(value, window.location.href).pathname.replace(/\/$/, '');
  } catch (_error) {
    return String(value || '').split('?')[0].replace(/\/$/, '');
  }
}

function sameCollection(first, second) {
  return normalizeCollectionUrl(first?.url) === normalizeCollectionUrl(second?.url);
}

function isSameTabNavigationClick(event) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function markProductNavigationForTop(link, event) {
  if (!link?.href || !isSameTabNavigationClick(event)) return;
  if (link.target && link.target !== '_self') return;

  try {
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;

    window.sessionStorage.setItem(productNavigationTopKey, JSON.stringify({
      path: url.pathname.replace(/\/$/, ''),
      time: Date.now(),
    }));

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  } catch (_error) {
    // Keep product navigation native if browser storage is unavailable.
  }
}

function normalizeCollection(collection) {
  if (!collection?.title || !collection?.url) return null;
  return {
    title: String(collection.title).trim(),
    url: normalizeCollectionUrl(collection.url),
  };
}

function dedupeCollectionTrail(trail) {
  const output = [];

  trail.map(normalizeCollection).filter(Boolean).forEach((collection) => {
    const existingIndex = output.findIndex((item) => sameCollection(item, collection));
    if (existingIndex >= 0) output.splice(existingIndex + 1);
    else output.push(collection);
  });

  return output;
}

function collectionFromUrl(value) {
  if (!value) return null;

  try {
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) return null;

    const match = url.pathname.match(/^\/collections\/([^/?#]+)/);
    if (!match?.[1] || match[1] === 'all') return null;

    const handle = decodeURIComponent(match[1]);
    return normalizeCollection({
      title: humanizeCollectionHandle(handle),
      url: `/collections/${encodeURIComponent(handle)}`,
    });
  } catch (_error) {
    return null;
  }
}

function currentCollectionFromPlp(plp) {
  return normalizeCollection({
    title: plp?.dataset.collectionTitle,
    url: plp?.dataset.collectionUrl,
  });
}

function isExternalReferrer() {
  if (!document.referrer) return false;

  try {
    return new URL(document.referrer).origin !== window.location.origin;
  } catch (_error) {
    return false;
  }
}

function isInternalReferrer() {
  if (!document.referrer) return false;

  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch (_error) {
    return false;
  }
}

function readCollectionTrail() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(sessionCollectionTrailKey) || '[]');
    return Array.isArray(stored) ? dedupeCollectionTrail(stored) : [];
  } catch (_error) {
    return [];
  }
}

function writeCollectionTrail(trail) {
  const normalizedTrail = dedupeCollectionTrail(trail);
  const current = normalizedTrail.at(-1);

  try {
    if (current) window.sessionStorage.setItem(sessionCollectionKey, JSON.stringify(current));
    window.sessionStorage.setItem(sessionCollectionTrailKey, JSON.stringify(normalizedTrail));
  } catch (_error) {
    // Keep the static breadcrumb when browser storage is unavailable.
  }

  return normalizedTrail;
}

function clearCollectionTrail() {
  try {
    window.sessionStorage.removeItem(sessionCollectionKey);
    window.sessionStorage.removeItem(sessionCollectionTrailKey);
  } catch (_error) {
    // Ignore storage failures and keep the static breadcrumb.
  }
}

function collectionTrailForPage(plp) {
  const currentCollection = currentCollectionFromPlp(plp);
  if (!currentCollection) return [];

  if (isExternalReferrer()) clearCollectionTrail();

  const storedTrail = readCollectionTrail();
  const referrerCollection = collectionFromUrl(document.referrer);
  const storedCurrentIndex = storedTrail.findIndex((collection) => sameCollection(collection, currentCollection));

  if (referrerCollection && !sameCollection(referrerCollection, currentCollection)) {
    const referrerIndex = storedTrail.findIndex((collection) => sameCollection(collection, referrerCollection));
    const baseTrail = referrerIndex >= 0 ? storedTrail.slice(0, referrerIndex + 1) : [referrerCollection];
    return dedupeCollectionTrail([...baseTrail, currentCollection]);
  }

  if (isInternalReferrer() && storedCurrentIndex >= 0) {
    return storedTrail.slice(0, storedCurrentIndex + 1);
  }

  return [currentCollection];
}

function appendBreadcrumbSeparator(nav) {
  const separator = document.createElement('span');
  separator.setAttribute('aria-hidden', 'true');
  separator.textContent = '/';
  nav.append(separator);
}

function renderCollectionBreadcrumb(plp, trail) {
  const breadcrumb = plp.querySelector('.sunco-plp-breadcrumb [data-sunco-breadcrumb]');
  if (!breadcrumb || trail.length === 0) return;

  const homeHref = breadcrumb.querySelector('[data-sunco-breadcrumb-home]')?.getAttribute('href') || '/';
  breadcrumb.textContent = '';

  const home = document.createElement('a');
  home.href = homeHref;
  home.dataset.suncoBreadcrumbHome = '';
  home.textContent = 'Home';
  breadcrumb.append(home);

  trail.forEach((collection, index) => {
    appendBreadcrumbSeparator(breadcrumb);

    if (index === trail.length - 1) {
      const current = document.createElement('span');
      current.dataset.suncoBreadcrumbCollection = '';
      current.textContent = collection.title;
      breadcrumb.append(current);
      return;
    }

    const link = document.createElement('a');
    link.href = collection.url;
    link.dataset.suncoBreadcrumbCollection = '';
    link.textContent = collection.title;
    breadcrumb.append(link);
  });
}

function updateSessionCollection(root = document) {
  const plp = root.matches?.('[data-sunco-plp]') ? root : root.querySelector('[data-sunco-plp]');
  if (!plp) return;

  const trail = writeCollectionTrail(collectionTrailForPage(plp));
  renderCollectionBreadcrumb(plp, trail);
}

function normalizeViewedProduct(product) {
  if (!product || typeof product !== 'object' || product.id == null) return null;
  const title = String(product.title || '').trim();
  const url = String(product.url || '').trim();
  if (!title || !url) return null;

  return {
    id: String(product.id),
    title,
    url,
    image: typeof product.image === 'string' ? product.image : product.image?.src || '',
    price: String(product.price || ''),
    card_html: product.card_html ? String(product.card_html) : '',
  };
}

function readRecentlyViewed() {
  try {
    const stored = JSON.parse(localStorage.getItem(recentlyViewedKey) || '[]');
    return Array.isArray(stored) ? stored.map(normalizeViewedProduct).filter(Boolean) : [];
  } catch (_error) {
    return [];
  }
}

function writeRecentlyViewed(products) {
  localStorage.setItem(recentlyViewedKey, JSON.stringify(products.map(normalizeViewedProduct).filter(Boolean).slice(0, 12)));
}

function recentlyViewedCardFragment(product, index) {
  if (!product.card_html) return null;

  const template = document.createElement('template');
  template.innerHTML = product.card_html;
  const fragment = template.content.cloneNode(true);
  const cards = fragment.querySelectorAll('[data-sunco-product-card]');

  if (!cards.length) return null;

  cards.forEach((card) => {
    card.dataset.suncoCardPosition = String(index + 1);
  });

  return fragment;
}

function renderRecentlyViewedCards(section, grid, products, designMode) {
  grid.replaceChildren();

  products.forEach((product, index) => {
    const fragment = recentlyViewedCardFragment(product, index);
    if (fragment) grid.append(fragment);
  });

  if (grid.children.length === 0) {
    section.hidden = !designMode;
    return;
  }

  setupProductCards(section);
  window.SuncoProductRails?.init?.(section);
  section.hidden = false;
}

function setupRecentlyViewed(root = document) {
  root.querySelectorAll('[data-sunco-plp-recently-viewed]').forEach((section) => {
    const data = parseJsonScript(section.querySelector('script[type="application/json"]')) || {};
    const grid = section.querySelector('[data-sunco-plp-recent-grid]');
    if (!grid) return;

    const products = readRecentlyViewed().slice(0, data.limit || 8);
    grid.replaceChildren();

    if (products.length === 0) {
      section.hidden = !data.design_mode;
      return;
    }

    renderRecentlyViewedCards(section, grid, products, data.design_mode);
  });
}

function sectionUrl(url, sectionId) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  return target;
}

function clearPlpRequestError(root) {
  root.querySelector('[data-sunco-plp-request-error]')?.remove();
}

function showPlpRequestError(root, message) {
  clearPlpRequestError(root);

  const status = document.createElement('p');
  status.dataset.suncoPlpRequestError = '';
  status.setAttribute('role', 'alert');
  status.setAttribute('aria-live', 'assertive');
  status.setAttribute('aria-atomic', 'true');

  const toolbar = root.querySelector('[data-sunco-plp-toolbar]');
  const main = root.querySelector('.sunco-plp__main');
  (toolbar?.parentElement || main || root).insertBefore(status, toolbar?.nextSibling || main?.firstChild || null);

  window.requestAnimationFrame(() => {
    status.textContent = message;
  });
}

function scrollToCollectionToolbar(root, behavior = 'smooth') {
  const toolbar = root.querySelector('[data-sunco-plp-toolbar]');
  if (!toolbar) return;

  const targetTop = Math.max(0, toolbar.getBoundingClientRect().top + window.scrollY - 86);
  window.scrollTo({
    top: targetTop,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior,
  });
}

function scheduleToolbarScroll(root) {
  const scroll = () => scrollToCollectionToolbar(root, 'auto');

  window.requestAnimationFrame(scroll);
  window.setTimeout(scroll, 300);
  window.setTimeout(scroll, 850);
}

async function replaceCollectionFragment(root, url, { pushState = true, scrollToToolbar = false, onFragmentReplaced = null } = {}) {
  const sectionId = root.dataset.sectionId;
  const currentFragment = root.querySelector('[data-sunco-plp-fragment]');
  if (!sectionId || !currentFragment) return;

  clearPlpRequestError(root);
  root.classList.add('is-loading');

  try {
    const response = await fetch(sectionUrl(url, sectionId).toString(), {
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`Collection request failed with status ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextFragment = parsed.querySelector('[data-sunco-plp-fragment]');
    if (!nextFragment) throw new Error('Collection response did not include the expected fragment');

    currentFragment.replaceWith(nextFragment);
    if (pushState) window.history.pushState({}, '', url);
    setupCollectionInteractions(root);
    updateSessionCollection(root);
    if (typeof onFragmentReplaced === 'function') onFragmentReplaced(root);
    if (scrollToToolbar) {
      scheduleToolbarScroll(root);
    }
  } catch (error) {
    console.error('Unable to update the product listing.', error);
    showPlpRequestError(root, 'We couldn\u2019t update the products. Please try again.');
  } finally {
    root.classList.remove('is-loading');
  }
}

function formUrl(form) {
  const nativeSearchScope = form.closest('[data-sunco-native-search]');
  const url = new URL(nativeSearchScope ? '/search' : form.action || window.location.href, window.location.origin);
  const params = new URLSearchParams(new FormData(form));

  url.search = '';
  params.delete('page');
  params.forEach((value, key) => {
    if (value !== '') url.searchParams.append(key, value);
  });

  return url;
}

function filterUrl(form, { includePrice = false } = {}) {
  const nativeSearchScope = form.closest('[data-sunco-native-search]');
  const url = new URL(nativeSearchScope ? '/search' : form.action || window.location.href, window.location.origin);
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams();

  new FormData(form).forEach((value, key) => {
    if (value === '' || key === 'page') return;
    if (!includePrice && (key.includes('.price.gte') || key.includes('.price.lte')) && !currentUrl.searchParams.has(key)) {
      return;
    }
    params.append(key, value);
  });

  if (includePrice) {
    form.querySelectorAll('[data-sunco-price-max]').forEach((input) => {
      const maxValue = input.dataset.rangeMax;
      if (input.value === maxValue) params.delete(input.name);
    });
    form.querySelectorAll('[data-sunco-price-min]').forEach((input) => {
      if (!input.value || Number(input.value) <= 0) params.delete(input.name);
    });
  }

  url.search = '';
  params.forEach((value, key) => {
    if (value !== '') url.searchParams.append(key, value);
  });

  return url;
}

function isFilterDrawerOpen(root) {
  return root.querySelector('[data-sunco-filter-drawer]')?.classList.contains('is-open') || false;
}

const filterDrawerTriggers = new WeakMap();

function filterDrawerFocusables(drawer) {
  if (!drawer) return [];

  return [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hidden || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      return element.getClientRects().length > 0;
    });
}

function focusFilterDrawer(drawer) {
  window.requestAnimationFrame(() => {
    if (!drawer?.classList.contains('is-open')) return;
    filterDrawerFocusables(drawer)[0]?.focus({ preventScroll: true });
  });
}

function trapFilterDrawerFocus(event, drawer) {
  const focusable = filterDrawerFocusables(drawer);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (!drawer.contains(active)) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function dismissInactiveToolbarSelects(event) {
  const activeSelect = event.target.closest?.('.sunco-plp-select');

  document.querySelectorAll('[data-sunco-plp] .sunco-plp-select[open]').forEach((select) => {
    if (select !== activeSelect) select.removeAttribute('open');
  });
}

function filterGroupLabel(filter) {
  return filter?.querySelector('summary span')?.textContent?.trim() || '';
}

function filterScrollElement(root) {
  const drawer = root.querySelector('[data-sunco-filter-drawer]');
  const form = root.querySelector('[data-sunco-filter-form]');

  if (window.matchMedia('(max-width: 989px)').matches) return form || drawer;
  return drawer || form;
}

function captureFilterState(root, sourceElement = null) {
  const scrollElement = filterScrollElement(root);
  const sourceFilter = sourceElement?.closest?.('.sunco-plp-filter');
  const openGroups = [...root.querySelectorAll('.sunco-plp-filter[open]')]
    .map(filterGroupLabel)
    .filter(Boolean);
  const sourceLabel = filterGroupLabel(sourceFilter);

  if (sourceLabel && !openGroups.includes(sourceLabel)) openGroups.push(sourceLabel);

  return {
    scrollTop: scrollElement?.scrollTop || 0,
    openGroups,
    sourceLabel,
  };
}

function restoreFilterState(root, state) {
  if (!state) return;

  const openGroups = new Set(state.openGroups || []);
  root.querySelectorAll('.sunco-plp-filter').forEach((filter) => {
    if (openGroups.has(filterGroupLabel(filter))) filter.setAttribute('open', '');
  });

  const scroll = () => {
    const scrollElement = filterScrollElement(root);
    if (scrollElement) scrollElement.scrollTop = state.scrollTop || 0;
  };

  window.requestAnimationFrame(scroll);
  window.setTimeout(scroll, 120);
  window.setTimeout(scroll, 360);
}

function restoreFilterChangeState(root, state, keepFilterDrawerOpen = false) {
  if (keepFilterDrawerOpen) restoreFilterDrawer(root);
  restoreFilterState(root, state);
}

function openFilterDrawer(root, trigger = null) {
  const drawer = root.querySelector('[data-sunco-filter-drawer]');
  if (!drawer) return;

  const returnFocus = trigger instanceof HTMLElement && trigger.isConnected
    ? trigger
    : root.querySelector('[data-sunco-filter-open]');
  if (returnFocus instanceof HTMLElement) filterDrawerTriggers.set(root, returnFocus);

  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('sunco-plp-filter-open');
  root.querySelectorAll('[data-sunco-filter-open]').forEach((button) => {
    button.setAttribute('aria-expanded', 'true');
  });
  focusFilterDrawer(drawer);
}

function closeFilterDrawer(root) {
  const drawer = root.querySelector('[data-sunco-filter-drawer]');
  const wasOpen = drawer?.classList.contains('is-open') || false;
  drawer?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('sunco-plp-filter-open');
  root.querySelectorAll('[data-sunco-filter-open]').forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
  });

  if (!wasOpen) return;

  const trigger = filterDrawerTriggers.get(root);
  filterDrawerTriggers.delete(root);
  window.requestAnimationFrame(() => {
    if (trigger instanceof HTMLElement && trigger.isConnected) {
      trigger.focus({ preventScroll: true });
    }
  });
}

function restoreFilterDrawer(root) {
  if (!isFilterDrawerOpen(root)) openFilterDrawer(root);
}

function setupFilterInteractions(root) {
  root.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-sunco-filter-open]');
    if (openButton) {
      trackPlpEvent(root, 'filter_open', { trigger: 'toolbar' });
      openFilterDrawer(root, openButton);
      return;
    }

    if (event.target.closest('[data-sunco-filter-close]')) {
      trackPlpEvent(root, 'filter_close', {
        trigger: event.target.closest('.sunco-plp-filters__footer') ? 'show_results' : 'close',
      });
      closeFilterDrawer(root);
      return;
    }

    const link = event.target.closest('[data-sunco-plp-link]');
    if (link?.href) {
      const keepFilterDrawerOpen = isFilterDrawerOpen(root) && Boolean(link.closest('[data-sunco-filter-drawer]'));
      if (link.hasAttribute('data-sunco-pagination-link')) {
        trackPlpEvent(root, 'pagination_click', {
          pagination_label: link.textContent.replace(/\s+/g, ' ').trim(),
          target_url: link.href,
        });
      } else if (link.closest('.sunco-plp-active-filters')) {
        trackPlpEvent(root, link.textContent.toLowerCase().includes('clear all') ? 'filter_clear_all' : 'filter_remove', {
          filter_value: link.textContent.replace('×', '').replace(/\s+/g, ' ').trim(),
          target_url: link.href,
        });
      }
      event.preventDefault();
      replaceCollectionFragment(root, link.href, {
        scrollToToolbar: link.hasAttribute('data-sunco-pagination-link'),
        onFragmentReplaced: keepFilterDrawerOpen ? restoreFilterDrawer : null,
      });
      if (!keepFilterDrawerOpen) closeFilterDrawer(root);
      return;
    }

    const more = event.target.closest('[data-sunco-filter-more]');
    if (more) {
      event.preventDefault();
      trackPlpEvent(root, 'filter_show_more', {
        filter_label: more.closest('.sunco-plp-filter')?.querySelector('summary span')?.textContent?.trim() || '',
      });
      const list = more.closest('.sunco-plp-filter__values');
      list?.querySelectorAll('[data-sunco-filter-extra]').forEach((item) => item.removeAttribute('hidden'));
      more.closest('.sunco-plp-filter__more')?.setAttribute('hidden', '');
      return;
    }

    const priceApply = event.target.closest('[data-sunco-price-apply]');
    if (priceApply) {
      event.preventDefault();
      const form = priceApply.closest('[data-sunco-filter-form]');
      const keepFilterDrawerOpen = isFilterDrawerOpen(root);
      const filterState = captureFilterState(root, priceApply);
      if (form) {
        trackPlpEvent(root, 'price_filter_apply', {
          min_price: form.querySelector('[data-sunco-price-min]')?.value || '',
          max_price: form.querySelector('[data-sunco-price-max]')?.value || '',
        });
        replaceCollectionFragment(root, filterUrl(form, { includePrice: true }).toString(), {
          onFragmentReplaced: (nextRoot) => restoreFilterChangeState(nextRoot, filterState, keepFilterDrawerOpen),
        });
      }
      return;
    }

    const perPage = event.target.closest('[data-sunco-per-page]');
    if (perPage) {
      event.preventDefault();
      const value = perPage.dataset.suncoPerPage;
      if (!value) return;
      trackPlpEvent(root, 'products_per_page_change', { products_per_page: value });
      clearPlpRequestError(root);

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes: { pagination: value } }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Items-per-page request failed with status ${response.status}`);
          window.location.reload();
        })
        .catch((error) => {
          console.error('Unable to update the items-per-page preference.', error);
          showPlpRequestError(root, 'We couldn\u2019t update the items-per-page setting. Please try again.');
        });
    }
  });

  root.addEventListener('change', (event) => {
    const filterInput = event.target.closest('[data-sunco-filter-form] input[type="checkbox"]');
    if (filterInput) {
      const form = filterInput.closest('[data-sunco-filter-form]');
      const keepFilterDrawerOpen = isFilterDrawerOpen(root);
      const filterState = captureFilterState(root, filterInput);
      trackPlpEvent(root, 'filter_change', filterInputPayload(filterInput));
      replaceCollectionFragment(root, filterUrl(form).toString(), {
        onFragmentReplaced: (nextRoot) => restoreFilterChangeState(nextRoot, filterState, keepFilterDrawerOpen),
      });
      return;
    }

    const sortInput = event.target.closest('[data-sunco-sort-form] input[name="sort_by"]');
    if (sortInput) {
      const form = sortInput.closest('[data-sunco-sort-form]');
      trackPlpEvent(root, 'sort_change', {
        sort_value: sortInput.value,
        sort_label: sortInput.closest('label')?.textContent?.trim() || sortInput.value,
      });
      replaceCollectionFragment(root, formUrl(form).toString());
    }
  });

  window.addEventListener('popstate', () => {
    replaceCollectionFragment(root, window.location.href, { pushState: false });
  });

  root.addEventListener('keydown', (event) => {
    if (!isFilterDrawerOpen(root)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeFilterDrawer(root);
    } else if (event.key === 'Tab') {
      trapFilterDrawerFocus(event, root.querySelector('[data-sunco-filter-drawer]'));
    }
  });
}

function hydrateSwatchLabels(root) {
  root.querySelectorAll('[data-sunco-swatches]').forEach((swatches) => {
    const selected = swatches.querySelector('[data-sunco-card-swatch].is-active');
    const selectedOption = swatches.closest('[data-sunco-product-card]')?.querySelector('[data-sunco-card-selected-option]');
    const optionName = swatches.dataset.optionName || 'Color';
    if (selected && selectedOption) selectedOption.textContent = `${optionName}: ${selected.dataset.title || ''}`;
  });
}

function setupSpecTooltips(root = document) {
  root.querySelectorAll('[data-sunco-plp-spec-item]').forEach((item) => {
    const value = item.querySelector('[data-sunco-plp-spec-value]');
    const hasFullText = Boolean(item.dataset.suncoSpecFull);
    const isOverflowing = value ? value.scrollWidth > value.clientWidth + 1 : false;

    item.classList.toggle('sunco-plp-card__spec--has-tooltip', hasFullText && isOverflowing);
    if (hasFullText && isOverflowing) {
      item.setAttribute('tabindex', '0');
    } else {
      item.removeAttribute('tabindex');
    }
  });
}

function cardImages(card) {
  if (card.suncoCardImages) return card.suncoCardImages;

  const script = card.querySelector('[data-sunco-card-images]');
  const images = parseJsonScript(script);
  card.suncoCardImages = Array.isArray(images) ? images.filter((image) => image?.src) : [];

  return card.suncoCardImages;
}

function normalizedImagePath(src) {
  if (!src) return '';

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.delete('width');
    return `${url.pathname}?${url.searchParams.toString()}`;
  } catch (_error) {
    return src;
  }
}

function setCardImage(card, index) {
  const images = cardImages(card);
  const image = card.querySelector('[data-sunco-card-image]');
  if (!image || images.length < 1) return;

  const nextIndex = ((index % images.length) + images.length) % images.length;
  const nextImage = images[nextIndex];

  image.src = nextImage.src;
  image.alt = nextImage.alt || '';
  image.removeAttribute('srcset');
  if (nextImage.width) image.width = nextImage.width;
  if (nextImage.height) image.height = nextImage.height;
  card.dataset.suncoCardImageIndex = String(nextIndex);
  syncCardImageDots(card, nextIndex);
}

function syncCardImageDots(card, index) {
  card.querySelectorAll('[data-sunco-card-image-dot]').forEach((dot) => {
    const active = Number.parseInt(dot.dataset.index || '0', 10) === index;
    dot.classList.toggle('is-active', active);
    dot.setAttribute('aria-current', active ? 'true' : 'false');
  });
}

function syncCardImageIndex(card, src) {
  const images = cardImages(card);
  if (images.length < 1 || !src) return;

  const selectedImage = normalizedImagePath(src);
  const index = images.findIndex((image) => normalizedImagePath(image.src) === selectedImage);
  if (index >= 0) {
    card.dataset.suncoCardImageIndex = String(index);
    syncCardImageDots(card, index);
  }
}

function slideCardImage(card, direction) {
  const images = cardImages(card);
  if (images.length < 2) return;

  const currentIndex = Number.parseInt(card.dataset.suncoCardImageIndex || '0', 10);
  const delta = direction === 'previous' ? -1 : 1;
  setCardImage(card, Number.isNaN(currentIndex) ? delta : currentIndex + delta);
}

function swipeState(card) {
  if (!card.suncoImageSwipe) card.suncoImageSwipe = {};
  return card.suncoImageSwipe;
}

function beginCardImageSwipe(event) {
  if (event.pointerType === 'mouse' || event.button > 0) return;

  const frame = event.target.closest('[data-sunco-card-image-frame]');
  const card = frame?.closest('[data-sunco-product-card]');
  if (!frame || !card || cardImages(card).length < 2) return;

  const state = swipeState(card);
  state.pointerId = event.pointerId;
  state.startX = event.clientX;
  state.startY = event.clientY;
  state.dragging = false;
  state.swiped = false;

  try {
    frame.setPointerCapture?.(event.pointerId);
  } catch (_error) {}
}

function moveCardImageSwipe(event) {
  const card = event.target.closest('[data-sunco-product-card]');
  const state = card ? swipeState(card) : null;
  if (!state || state.pointerId !== event.pointerId) return;

  const deltaX = event.clientX - state.startX;
  const deltaY = event.clientY - state.startY;

  if (!state.dragging && Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
    state.dragging = true;
  }

  if (state.dragging) event.preventDefault();
}

function endCardImageSwipe(event) {
  const card = event.target.closest('[data-sunco-product-card]');
  const state = card ? swipeState(card) : null;
  if (!state || state.pointerId !== event.pointerId) return;

  const deltaX = event.clientX - state.startX;
  const deltaY = event.clientY - state.startY;
  const isSwipe = Math.abs(deltaX) > 36 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35;

  if (isSwipe) {
    slideCardImage(card, deltaX < 0 ? 'next' : 'previous');
    trackPlpEvent(card.closest('[data-sunco-plp]'), 'image_swipe', {
      direction: deltaX < 0 ? 'next' : 'previous',
      image_index: card.dataset.suncoCardImageIndex || '',
      ...productCardPayload(card),
    });
    state.swiped = true;
    card.dataset.suncoSuppressImageClick = 'true';
    window.setTimeout(() => {
      delete card.dataset.suncoSuppressImageClick;
      state.swiped = false;
    }, 350);
  }

  state.pointerId = null;
  state.dragging = false;
}

function setupImageSliderState(root) {
  root.querySelectorAll('[data-sunco-product-card]').forEach((card) => {
    const currentIndex = Number.parseInt(card.dataset.suncoCardImageIndex || '0', 10);
    syncCardImageDots(card, Number.isNaN(currentIndex) ? 0 : currentIndex);
  });
}

function copyText(value) {
  if (!value) return Promise.reject(new Error('Nothing to copy'));

  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? resolve() : reject(new Error('Copy command failed'));
    } catch (error) {
      document.body.removeChild(textarea);
      reject(error);
    }
  });
}

const cardVariantAnnouncementTimers = new WeakMap();

function setupSwatches(root) {
  if (root.dataset.suncoSwatchesReady === 'true') {
    hydrateSwatchLabels(root);
    setupSpecTooltips(root);
    setupImageSliderState(root);
    return;
  }

  root.dataset.suncoSwatchesReady = 'true';

  root.addEventListener('pointerdown', beginCardImageSwipe);
  root.addEventListener('pointermove', moveCardImageSwipe);
  root.addEventListener('pointerup', endCardImageSwipe);
  root.addEventListener('pointercancel', endCardImageSwipe);

  root.addEventListener('click', (event) => {
    const imageLink = event.target.closest('.sunco-plp-card__image-link');
    const imageCard = imageLink?.closest('[data-sunco-product-card]');
    if (imageCard?.dataset.suncoSuppressImageClick === 'true') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const quickAddTrigger = event.target.closest('[data-sunco-mobile-quick-add-trigger]');
    if (quickAddTrigger) {
      event.preventDefault();
      event.stopPropagation();
      handleMobileQuickAddTrigger(root, quickAddTrigger);
      return;
    }

    const quickAddOption = event.target.closest('[data-sunco-mobile-quick-add-option]');
    if (quickAddOption) {
      event.preventDefault();
      event.stopPropagation();
      const card = quickAddOption.closest('[data-sunco-product-card]');
      if (card) handleMobileQuickAddOption(card, quickAddOption);
      return;
    }

    const quickAddSubmit = event.target.closest('[data-sunco-mobile-quick-add-submit]');
    if (quickAddSubmit) {
      event.preventDefault();
      event.stopPropagation();
      handleMobileQuickAddSubmit(root, quickAddSubmit);
      return;
    }

    const quickAddQuantityAction = event.target.closest('[data-sunco-mobile-quick-add-quantity-action]');
    if (quickAddQuantityAction) {
      event.preventDefault();
      event.stopPropagation();
      handleMobileQuickAddQuantityAction(quickAddQuantityAction);
      return;
    }

    const productLink = event.target.closest('[data-sunco-card-link]');
    if (productLink) {
      const card = productLink.closest('[data-sunco-product-card]');
      markProductNavigationForTop(productLink, event);
      let linkLocation = 'card';
      if (productLink.classList.contains('sunco-plp-card__button')) linkLocation = 'cta';
      if (productLink.classList.contains('sunco-plp-card__title')) linkLocation = 'title';
      if (productLink.classList.contains('sunco-plp-card__image-link')) linkLocation = 'image';
      trackPlpEvent(root, 'product_click', {
        link_location: linkLocation,
        target_url: productLink.href,
        ...productCardPayload(card),
      });
    }

    const imageNav = event.target.closest('[data-sunco-card-image-nav]');
    if (imageNav) {
      event.preventDefault();
      event.stopPropagation();

      const card = imageNav.closest('[data-sunco-product-card]');
      if (card) {
        slideCardImage(card, imageNav.dataset.direction);
        trackPlpEvent(root, 'image_nav_click', {
          direction: imageNav.dataset.direction || '',
          image_index: card.dataset.suncoCardImageIndex || '',
          ...productCardPayload(card),
        });
      }
      return;
    }

    const imageDot = event.target.closest('[data-sunco-card-image-dot]');
    if (imageDot) {
      event.preventDefault();
      event.stopPropagation();

      const card = imageDot.closest('[data-sunco-product-card]');
      const index = Number.parseInt(imageDot.dataset.index || '0', 10);
      if (card && !Number.isNaN(index)) {
        setCardImage(card, index);
        trackPlpEvent(root, 'image_dot_click', {
          image_index: String(index),
          ...productCardPayload(card),
        });
      }
      return;
    }

    const skuCopy = event.target.closest('[data-sunco-card-sku-copy]');
    if (skuCopy) {
      event.preventDefault();
      const value = skuCopy.dataset.suncoCopyValue;

      copyText(value)
        .then(() => {
          skuCopy.classList.add('is-copied');
          window.setTimeout(() => skuCopy.classList.remove('is-copied'), 1200);
        })
        .catch(() => {});
      trackPlpEvent(root, 'sku_copy', productCardPayload(skuCopy.closest('[data-sunco-product-card]')));
      return;
    }

    const more = event.target.closest('[data-sunco-swatch-more]');
    if (more) {
      event.preventDefault();
      const swatches = more.closest('[data-sunco-swatches]');
      if (more.hasAttribute('data-sunco-mobile-swatch-more')) {
        swatches?.setAttribute('data-sunco-mobile-expanded', 'true');
      }
      trackPlpEvent(root, 'swatch_show_more', productCardPayload(more.closest('[data-sunco-product-card]')));
      swatches?.querySelectorAll('[data-sunco-swatch-extra]').forEach((item) => item.removeAttribute('hidden'));
      more.closest('.sunco-plp-swatches__more')?.setAttribute('hidden', '');
      return;
    }

    const button = event.target.closest('[data-sunco-card-swatch]');
    if (!button) return;

    const card = button.closest('[data-sunco-product-card]');
    if (!card) return;
    const quickAddPanel = card.querySelector('[data-sunco-mobile-quick-add-panel]');
    const openQuickAdd = mobileQuickAddMedia.matches && quickAddPanel && !quickAddPanel.hidden
      ? { data: getQuickAddData(card), selections: card.suncoQuickAddSelections, quantity: getMobileQuickAddQuantity(card) }
      : null;
    // Restore listing state before the swatch updates it, then preview the retained pack.
    closeMobileQuickAddPanel(card);
    trackPlpEvent(root, 'swatch_click', {
      ...productCardPayload(card),
      option_name: button.closest('[data-sunco-swatches]')?.dataset.optionName || 'Color',
      option_value: button.dataset.title || '',
      variant_id: button.dataset.variantId || '',
      target_url: button.dataset.variantUrl || '',
    });

    card.querySelectorAll('[data-sunco-card-swatch]').forEach((swatch) => {
      const active = swatch === button;
      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const image = card.querySelector('[data-sunco-card-image]');
    if (image && button.dataset.image) {
      image.src = button.dataset.image;
      image.removeAttribute('srcset');
      syncCardImageIndex(card, button.dataset.image);
    }

    const sku = card.querySelector('[data-sunco-card-sku]');
    if (sku && button.dataset.sku) sku.textContent = button.dataset.sku;
    if (button.dataset.variantId) card.dataset.variantId = button.dataset.variantId;

    const skuCopyButton = card.querySelector('[data-sunco-card-sku-copy]');
    if (skuCopyButton && button.dataset.sku) {
      skuCopyButton.dataset.suncoCopyValue = button.dataset.sku;
      skuCopyButton.setAttribute('aria-label', `Copy SKU ${button.dataset.sku}`);
    }

    const selectedOption = card.querySelector('[data-sunco-card-selected-option]');
    const optionName = button.closest('[data-sunco-swatches]')?.dataset.optionName || 'Color';
    if (selectedOption && button.dataset.title) {
      selectedOption.textContent = `${optionName}: ${button.dataset.title}`;
    }

    const links = card.querySelectorAll('[data-sunco-card-link]');
    if (button.dataset.variantUrl) {
      links.forEach((link) => {
        link.href = button.dataset.variantUrl;
      });
      card.dataset.productUrl = button.dataset.variantUrl;
    }

    const price = card.querySelector('[data-sunco-card-price]');
    if (price && button.dataset.price && button.dataset.oneOption === 'true') {
      price.textContent = button.dataset.price;
    }

    const comparePrice = card.querySelector('[data-sunco-card-compare-price]');
    const priceMeta = card.querySelector('[data-sunco-card-price-meta]');
    const savings = card.querySelector('[data-sunco-card-savings]');
    if (comparePrice && button.dataset.oneOption === 'true') {
      if (button.dataset.compareAtPrice) {
        comparePrice.textContent = button.dataset.compareAtPrice;
        if (savings) savings.textContent = button.dataset.savings ? `Save ${button.dataset.savings}` : '';
        if (savings) savings.hidden = !button.dataset.savings;
        if (priceMeta) priceMeta.hidden = false;
      } else {
        comparePrice.textContent = '';
        if (savings) savings.textContent = '';
        if (savings) savings.hidden = true;
        if (priceMeta) priceMeta.hidden = true;
      }
    }

    if (openQuickAdd?.data) {
      card.suncoQuickAddSelections = openQuickAdd.selections;
      renderMobileQuickAddPanel(card, openQuickAdd.data, openQuickAdd.quantity);
      quickAddPanel.removeAttribute('hidden');
      card.querySelector('[data-sunco-mobile-quick-add-trigger]')?.setAttribute('aria-expanded', 'true');
    }

    let status = card.querySelector('[data-sunco-card-variant-status]');
    if (!status) {
      status = document.createElement('span');
      status.className = 'visually-hidden';
      status.dataset.suncoCardVariantStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      card.append(status);
    }

    const announcement = [`${optionName} ${button.dataset.title || ''} selected`];
    if (button.dataset.oneOption === 'true' && button.dataset.price) {
      announcement.push(`Price ${button.dataset.price}`);
    }
    if (button.classList.contains('is-unavailable')) announcement.push('Unavailable');

    window.clearTimeout(cardVariantAnnouncementTimers.get(card));
    status.textContent = '';
    const announcementTimer = window.setTimeout(() => {
      status.textContent = `${announcement.join('. ')}.`;
    }, 40);
    cardVariantAnnouncementTimers.set(card, announcementTimer);
  });

  hydrateSwatchLabels(root);
  setupSpecTooltips(root);
  setupImageSliderState(root);
}

function setupCollectionInteractions(root = document) {
  const plp = root.matches?.('[data-sunco-plp]') ? root : root.querySelector('[data-sunco-plp]');
  if (!plp || plp.dataset.suncoPlpReady === 'true') {
    if (plp) setupSwatches(plp);
    return;
  }

  plp.dataset.suncoPlpReady = 'true';
  setupFilterInteractions(plp);
  setupSwatches(plp);
  setupSpecTooltips(plp);
}

function setupProductCards(root = document) {
  const scopes = [];

  if (root.matches?.('[data-sunco-product-card-scope], [data-sunco-plp]')) {
    scopes.push(root);
  }

  root.querySelectorAll?.('[data-sunco-product-card-scope], [data-sunco-plp]').forEach((scope) => {
    if (!scopes.includes(scope)) scopes.push(scope);
  });

  if (!scopes.length && root.dataset && root.querySelector?.('[data-sunco-product-card]')) {
    scopes.push(root);
  }

  scopes.forEach((scope) => setupSwatches(scope));
  if (!scopes.length) setupSpecTooltips(root);
}

window.SuncoPlp = window.SuncoPlp || {};
window.SuncoPlp.setupProductCards = setupProductCards;

document.addEventListener('click', dismissInactiveToolbarSelects);

document.addEventListener('DOMContentLoaded', () => {
  updateSessionCollection(document);
  setupCollectionInteractions(document);
  setupRecentlyViewed(document);
});

let specTooltipResizeFrame = 0;
window.addEventListener(
  'resize',
  () => {
    if (specTooltipResizeFrame) return;
    specTooltipResizeFrame = window.requestAnimationFrame(() => {
      specTooltipResizeFrame = 0;
      document.querySelectorAll('[data-sunco-product-card-scope], [data-sunco-plp]').forEach(setupSpecTooltips);
    });
  },
  { passive: true }
);
